import { useCallback, useEffect, useState } from 'react';

import { SelectChangeEvent } from '@mui/material';
import { FormikErrors, useFormik } from 'formik';
import { Dayjs } from 'dayjs';

import FormCreatorComponent from './formCreatorComponent';

import { FormFieldData } from '../../../constants/types';
import {
  TDosageFrequency,
  TMedicationInitialValues,
} from 'pages/medications/addMedication/types';
import { TAllergyInitialValues } from '../../../pages/allergies/create/types';

type HandleAllergySaveType = (formData: TAllergyInitialValues) => void;
type HandleMedicationSaveType = (formData: TMedicationInitialValues) => void;
type FormValues = TAllergyInitialValues | TMedicationInitialValues;

interface FormCreatorProps {
  isModalOpen: boolean;
  formFieldData: FormFieldData[];
  initialValues: FormValues;
  setIsModalOpen: (isOpen: boolean) => void;
  validationSchema: (key: string) => object;
  handleSave: HandleAllergySaveType | HandleMedicationSaveType;
}

const FormCreator: React.FC<FormCreatorProps> = ({
  isModalOpen,
  formFieldData,
  initialValues,
  handleSave,
  validationSchema,
  setIsModalOpen,
}) => {
  const [index, setIndex] = useState<number>(0);

  const handleContinue = () => {
    if (index < formFieldData.length - 1) setIndex(index + 1);
    else handleSubmit();
  };

  const handleBack = () => {
    if (index > 0) setIndex(index - 1);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    resetForm();
    setIndex(0);
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    handleChange({
      target: {
        name: event.target.name,
        value: event.target.value,
      },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleSwitch = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleChange({
      target: {
        name: event.target.name,
        value: event.target.checked,
      },
    });
  };

  const isAllergyInitialValues = (
    values: typeof initialValues
  ): values is TAllergyInitialValues => {
    return (values as TAllergyInitialValues).severity !== undefined;
  };

  const isMedicationInitialValues = (
    values: typeof initialValues
  ): values is TMedicationInitialValues => {
    return (values as TMedicationInitialValues).dosage !== undefined;
  };

  const {
    values,
    isValid,
    dirty,
    touched,
    errors,
    handleChange,
    handleSubmit,
    handleBlur,
    resetForm,
    setFieldValue,
  } = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema(formFieldData[index].name),
    onSubmit: (formData) => {
      if (isAllergyInitialValues(formData) && handleSave instanceof Function) {
        (handleSave as HandleAllergySaveType)(formData);
      } else if (
        isMedicationInitialValues(formData) &&
        handleSave instanceof Function
      ) {
        (handleSave as HandleMedicationSaveType)(formData);
      }
    },
  });

  const handleDateChange = (date: Dayjs | null) => {
    setFieldValue('reminderEndDate', date?.toDate() ?? '');
  };

  const handleCheckBoxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const dosageFrequency = (values as TMedicationInitialValues)
      .dosageFrequency as TDosageFrequency;

    const { name, checked } = event.target;

    setFieldValue('dosageFrequency', {
      ...dosageFrequency,
      [name]: checked,
    });
  };

  const isFieldTouched = useCallback(() => {
    const { name, required } = formFieldData[index];

    if (required) {
      const fieldTouched = touched[name as keyof FormValues];
      const fieldValue = values[name as keyof FormValues];
      return fieldTouched !== undefined && fieldValue !== '';
    }

    return true;
  }, [formFieldData, index, touched, values]);

  useEffect(() => {
    if (!isModalOpen) {
      setIndex(0);
      resetForm();
    }
  }, [isModalOpen, resetForm]);

  return (
    <FormCreatorComponent
      index={index}
      isModalOpen={isModalOpen}
      isValid={isValid}
      dirty={dirty}
      values={values}
      errors={errors as FormikErrors<FormValues>}
      isFieldTouched={isFieldTouched()}
      formFieldData={formFieldData[index]}
      handleBlur={handleBlur}
      handleChange={handleChange}
      handleSubmit={handleSubmit}
      handleBack={handleBack}
      handleClose={handleClose}
      handleContinue={handleContinue}
      handleSelectChange={handleSelectChange}
      handleSwitch={handleSwitch}
      handleDateChange={handleDateChange}
      handleCheckBoxChange={handleCheckBoxChange}
    />
  );
};

export default FormCreator;
